// src/app/pages/merch/merch.component.ts

import {
  AfterViewInit,
  Component,
  OnInit
} from '@angular/core';

import {
  DomSanitizer,
  SafeResourceUrl
} from '@angular/platform-browser';

import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { SeoService } from '../../services/seo.service';

declare global {
  interface Window {
    paypal?: {
      HostedButtons: (options: {
        hostedButtonId: string;
      }) => {
        render: (selector: string) => void;
      };
    };
  }
}

export interface MerchItem {
  id: string;
  name: string;
  description: string;
  image: string;

  // Physical campaign inventory
  stockAvailable: boolean;
  stockQuantity?: number;

  // Customer-facing inventory status
  stockLabel?: string;

  // Can this product currently be purchased somewhere?
  purchasable: boolean;

  // Optional Bonfire fallback
  bonfireUrl?: string;

  // Optional PayPal Hosted Button
  paypalHostedButtonId?: string;

  badge?: string;
  price?: number;
}

@Component({
  selector: 'app-merch',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './merch.component.html',
  styleUrls: ['./merch.component.css']
})
export class MerchComponent implements OnInit, AfterViewInit {

  private readonly bonfireUrl =
    'https://www.bonfire.com/store/lydia-christensen-for-us-senate/';

  items: MerchItem[] = [

    // ---------------------------------------------------------
    // YARD SIGN
    // ---------------------------------------------------------

    {
      id: 'campaign-yard-sign',
      name: 'Lydia Christensen for U.S. Senate Double-Sided Yard Sign',
      description:
        '18" × 24" double-sided corrugated plastic yard sign featuring Lydia Christensen for U.S. Senate campaign artwork. Includes a wire/metal ground stake and is ready to display outdoors.',
      price: 24,
      image: '../../assets/FullsizePoliticalPoster.pdf',
      stockAvailable: true,
      stockQuantity: 20,
      stockLabel: 'Available',
      purchasable: true,
      paypalHostedButtonId: 'LFA4Z3DPU68FG',
      badge: 'Double-Sided'
    },

    // ---------------------------------------------------------
    // CAMPAIGN LOGO SHIRT
    // ---------------------------------------------------------

    {
      id: 'campaign-logo-tshirt',
      name: 'Lydia Christensen Campaign Logo T-Shirt',
      description:
        'Premium campaign T-shirt featuring the Lydia Christensen campaign logo. Professionally printed and available while supplies last.',
      price: 35,
      image: '../../assets/lydia-christensen-campaign-logo-tshirt.jpg',
      stockAvailable: true,
      stockQuantity: undefined,
      stockLabel: 'In Stock',
      purchasable: true,
      paypalHostedButtonId: 'AQPGQFR4EKFQL',
      bonfireUrl: this.bonfireUrl,
      badge: 'In Stock'
    },

    // ---------------------------------------------------------
    // FROG ARMY SHIRT
    // ---------------------------------------------------------

    {
      id: 'frog-army-supporter-tshirt',
      name: 'Frog Army Supporter T-Shirt',
      description:
        'Premium supporter T-shirt featuring the Frog Army design. Professionally printed and available while supplies last.',
      price: 35,
      image: '../../assets/lydia-christensen-frog-army-supporter-tshirt.jpg',
      stockAvailable: true,
      stockQuantity: undefined,
      stockLabel: 'In Stock',
      purchasable: true,
      paypalHostedButtonId: 'UWKBV464ATUX6',
      bonfireUrl: this.bonfireUrl,
      badge: 'In Stock'
    },

    // ---------------------------------------------------------
    // LYDIA FOR SENATE SHIRT
    // ---------------------------------------------------------

    {
      id: 'lydia-for-senate-tshirt',
      name: 'Lydia Christensen for Senate T-Shirt',
      description:
        'Premium T-shirt featuring the Lydia Christensen for Senate text logo. Campaign stock is currently sold out, but orders can still be placed through the campaign.',
      price: 35,
      image: '../../assets/lydia-christensen-for-senate-tshirt.jpg',
      stockAvailable: false,
      stockQuantity: undefined,
      stockLabel: 'Available to Order',
      purchasable: true,
      paypalHostedButtonId: 'LEL74YSMS2YKL',
      bonfireUrl: this.bonfireUrl,
      badge: 'Available to Order'
    },

    // ---------------------------------------------------------
    // MUSHROOM FROG SHIRT
    // ---------------------------------------------------------

    {
      id: 'mushroom-frog-supporter-tshirt',
      name: 'Mushroom Frog Supporter T-Shirt',
      description:
        'Premium supporter T-shirt featuring the Mushroom Frog design. Professionally printed and available while supplies last.',
      price: 35,
      image: '../../assets/lydia-christensen-mushroom-frog-supporter-tshirt.jpg',
      stockAvailable: true,
      stockQuantity: undefined,
      stockLabel: 'In Stock',
      purchasable: true,
      paypalHostedButtonId: 'X6E9UGQDE34JW',
      bonfireUrl: this.bonfireUrl,
      badge: 'In Stock'
    },

    // ---------------------------------------------------------
    // BUMPER STICKER
    // ---------------------------------------------------------

    {
      id: 'bumper-sticker',
      name: 'Lydia Christensen Campaign Bumper Sticker',
      description:
        'Official Lydia Christensen campaign bumper sticker. Available while supplies last.',
      price: 15,
      image: '../../assets/lydia-christensen-campaign-bumper-sticker.png',
      stockAvailable: true,
      stockQuantity: undefined,
      stockLabel: 'In Stock',
      purchasable: true,
      paypalHostedButtonId: '48HYBJSXWZJ4U',
      badge: 'In Stock'
    },

    // ---------------------------------------------------------
    // CAMPAIGN RALLY SIGN
    // ---------------------------------------------------------

    {
      id: 'campaign-rally-sign',
      name: 'Lydia Christensen for U.S. Senate Rally Sign',
      description:
        'Campaign rally sign featuring the Lydia Christensen for U.S. Senate design, printed to order for supporters to hold at rallies, speeches, and campaign events.',
      price: 10,
      image: '../../assets/lydia-christensen-for-senate-rally-sign.png',
      stockAvailable: false,
      stockQuantity: undefined,
      stockLabel: 'Available to Order',
      purchasable: true,
      paypalHostedButtonId: '2N99NWJ3R6ZCQ',
      badge: 'Available to Order'
    },

    // ---------------------------------------------------------
    // FRAMED CAMPAIGN LOGO POSTER
    // ---------------------------------------------------------

    {
      id: 'campaign-logo-framed-poster',
      name: 'Lydia Christensen Campaign Logo Framed Poster',
      description:
        'Framed campaign poster featuring the Lydia Christensen campaign logo design and campaign quote.',
      price: 25,
      image: '../../assets/lydia-christensen-campaign-logo-poster.png',
      stockAvailable: true,
      stockQuantity: 5,
      stockLabel: '5 Available',
      purchasable: true,
      paypalHostedButtonId: '95FBVH78NKT9U',
      badge: '5 Available'
    },

    // ---------------------------------------------------------
    // FRAMED FROG ARMY POSTER
    // ---------------------------------------------------------

    {
      id: 'frog-army-framed-poster',
      name: 'Frog Army Framed Poster',
      description:
        'Framed campaign poster featuring the Frog Army design and campaign quote.',
      price: 25,
      image: '../../assets/lydia-christensen-frog-army-poster.png',
      stockAvailable: true,
      stockQuantity: 5,
      stockLabel: '5 Available',
      purchasable: true,
      paypalHostedButtonId: '93WRMXAQ69EYW',
      badge: '5 Available'
    },

    // ---------------------------------------------------------
    // FRAMED MUSHROOM FROG POSTER
    // ---------------------------------------------------------

    {
      id: 'mushroom-frog-framed-poster',
      name: 'Mushroom Frog Framed Poster',
      description:
        'Framed campaign poster featuring the Mushroom Frog design and campaign quote.',
      price: 25,
      image: '../../assets/lydia-christensen-mushroom-frog-poster.png',
      stockAvailable: true,
      stockQuantity: 5,
      stockLabel: '5 Available',
      purchasable: true,
      paypalHostedButtonId: 'P4B6FPHCRPU4A',
      badge: '5 Available'
    },

    // ---------------------------------------------------------
    // FRAMED MEDIEVAL FROG WARRIOR POSTER
    // ---------------------------------------------------------

    {
      id: 'medieval-frog-warrior-framed-poster',
      name: 'Medieval Frog Warrior Framed Poster',
      description:
        'Framed campaign poster featuring the Medieval Frog Warrior design and campaign quote.',
      price: 25,
      image: '../../assets/lydia-christensen-medieval-frog-warrior-poster.png',
      stockAvailable: true,
      stockQuantity: 5,
      stockLabel: '5 Available',
      purchasable: true,
      paypalHostedButtonId: '632YJDKKXDYV8',
      badge: '5 Available'
    },

    // ---------------------------------------------------------
    // ONE-OF-ONE SIGNED POSTER
    // ---------------------------------------------------------

    {
      id: 'signed-become-ungovernable-poster',
      name: 'Become Ungovernable Framed and Signed Poster | One of One',
      description:
        'One-of-one "Become Ungovernable" campaign poster personally signed by Lydia Christensen in metallic gold or silver. A unique collector’s piece from the 2026 U.S. Senate campaign. Only one available.',
      price: 1000,
      image: '../../assets/lydia-christensen-signed-become-ungovernable-poster.png',
      stockAvailable: true,
      stockQuantity: 1,
      stockLabel: 'ONE OF ONE',
      purchasable: true,
      paypalHostedButtonId: 'LZ34KY455LJWW',
      badge: 'One of One'
    }

  ];

  private readonly baseUrl =
    'https://www.lydialynnchristensen.com';

  private readonly merchPageId =
    `${this.baseUrl}/merch#store`;

  private readonly productListId =
    `${this.baseUrl}/merch#products`;

  constructor(
    private seo: SeoService,
    private sanitizer: DomSanitizer
  ) {}

  ngOnInit(): void {

    /*
     * The merch page is a collection/store page rather than an individual
     * product page. Google recommends individual product URLs for Product
     * merchant-listing eligibility, so this page primarily uses CollectionPage
     * + ItemList structured data.
     */

    this.seo.update(
      'Lydia Christensen Campaign Merchandise | Official Store',
      'Shop official Lydia Christensen for U.S. Senate campaign merchandise, including T-shirts, yard signs, bumper stickers, rally signs, and framed posters.',
      'merch'
    );

    this.seo.setStructuredData({

      '@context': 'https://schema.org',

      '@graph': [

        // -----------------------------------------------------
        // COLLECTION PAGE
        // -----------------------------------------------------

        {
          '@type': 'CollectionPage',
          '@id': this.merchPageId,
          'url': `${this.baseUrl}/merch`,
          'name': 'Lydia Christensen Campaign Merchandise | Official Store',
          'description':
            'Official Lydia Christensen for U.S. Senate campaign merchandise, including campaign T-shirts, yard signs, bumper stickers, rally signs, and framed posters.',
          'isPartOf': {
            '@id': this.seo.websiteId
          },
          'about': {
            '@id': this.seo.campaignId
          },
          'publisher': {
            '@id': this.seo.campaignId
          },
          'mainEntity': {
            '@id': this.productListId
          },
          'inLanguage': 'en-US'
        },

        // -----------------------------------------------------
        // ITEM LIST
        // -----------------------------------------------------

        {
          '@type': 'ItemList',
          '@id': this.productListId,
          'name': 'Lydia Christensen Campaign Merchandise',
          'description':
            'Official campaign merchandise available from the Lydia Christensen for U.S. Senate campaign.',
          'numberOfItems': this.items.length,
          'itemListOrder': 'https://schema.org/ItemListOrderAscending',
          'itemListElement': this.items.map((item, index) => ({
            '@type': 'ListItem',
            'position': index + 1,
            'name': item.name,
            'url': `${this.baseUrl}/merch#${item.id}`,
            'item': this.buildProductStructuredData(item)
          }))
        }

      ]

    });

  }

  ngAfterViewInit(): void {
    this.renderPayPalButtons();
  }

  getSafePdfUrl(path: string): SafeResourceUrl {
    return this.sanitizer.bypassSecurityTrustResourceUrl(
      path
    );
  }

  getPrice(item: MerchItem): number {
    return item.price ?? 0;
  }

  getOrderSubject(item: MerchItem): string {
    return `Merch Order — ${item.name}`;
  }

  /**
   * Render every PayPal Hosted Button that has been configured.
   *
   * To add another PayPal product later, simply add
   * paypalHostedButtonId to that product.
   */
  private renderPayPalButtons(): void {

    const paypal = window.paypal;

    if (!paypal) {
      console.error(
        'PayPal SDK has not loaded. The campaign store buttons could not be rendered.'
      );
      return;
    }

    for (const item of this.items) {

      if (!item.paypalHostedButtonId) {
        continue;
      }

      const containerId =
        `paypal-container-${item.paypalHostedButtonId}`;

      const container =
        document.getElementById(containerId);

      if (!container) {
        console.error(
          `PayPal container was not found for ${item.name}.`
        );
        continue;
      }

      // Prevent duplicate rendering.
      if (container.childElementCount > 0) {
        continue;
      }

      paypal.HostedButtons({
        hostedButtonId: item.paypalHostedButtonId
      }).render(`#${containerId}`);

    }

  }

  /**
   * Convert the relative asset path used by the Angular template
   * into an absolute /assets/... URL used by structured data.
   */
  private getAbsoluteAssetUrl(path: string): string {

    const assetsIndex = path.indexOf('assets/');

    if (assetsIndex >= 0) {
      return `${this.baseUrl}/${path.substring(assetsIndex)}`;
    }

    return `${this.baseUrl}/${path.replace(/^\/+/, '')}`;
  }

  private buildProductStructuredData(item: MerchItem): any {

    const productUrl =
      `${this.baseUrl}/merch#${item.id}`;

    const product: any = {
      '@type': 'Product',
      '@id': productUrl,
      'name': item.name,
      'description': item.description,
      'url': productUrl,
      'category': 'Campaign Merchandise'
    };

    /*
     * Google requires product images to be crawlable image files.
     * The yard sign currently uses a PDF as its visual preview, so don't
     * incorrectly advertise that PDF as a Product image.
     */
    if (!item.image.toLowerCase().endsWith('.pdf')) {
      product.image =
        this.getAbsoluteAssetUrl(item.image);
    }

    /*
     * Only advertise an Offer when the product has a known campaign
     * price and is currently purchasable.
     */
    if (
      item.price !== undefined &&
      item.purchasable
    ) {

      product.offers = {
        '@type': 'Offer',
        'price': item.price,
        'priceCurrency': 'USD',
        'availability': item.stockAvailable
          ? 'https://schema.org/InStock'
          : 'https://schema.org/PreOrder',
        'itemCondition': 'https://schema.org/NewCondition',
        'url': productUrl
      };

    }

    return product;
  }

}